import { faInputText } from "@fortawesome/pro-regular-svg-icons"
import { faFont, faImage, faMagnifyingGlass, faPalette } from "@fortawesome/pro-solid-svg-icons"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import React from "react"
import { connect } from "react-redux"
import { ContentBox } from "ui/components/ContentBox"
import { FlexBox } from "ui/components/FlexBox"
import { PageHeader } from "ui/components/main-layout/PageHeader"
import { UiConfigColors } from "ui/configuration/components/UiConfigColors"
import { UiConfigFilesUpload } from "ui/configuration/components/UiConfigFilesUpload"
import { UiConfigPreview } from "ui/configuration/components/UiConfigPreview"
import {
  IUIConfiguration,
  UI_CONFIG_FONT_DIV_ID,
  UI_CONFIG_PAGE_DIV_ID,
  UI_CONFIG_PREVIEW_DIV_ID
} from "ui/configuration/model"
import { PAGE_HEADER_HEIGHT_IN_REM } from "ui/model"
import { isPhoneScreen } from "ui/tools/responsive"
import { hasUiConfigurationChanges, resetUiConfigChanges } from "../tools/undo-redo"
import { UiConfigFont } from "./UiConfigFont"
import { UiConfigHeader } from "./UiConfigHeader"
import { UiConfigPageTitle } from "./UiConfigPageTitle"
import { UiConfigTranslations } from "./UiConfigTranslations"

interface IAdminUiConfigurationPageProps {
  uiConfig: IUIConfiguration
  tempUiConfig: IUIConfiguration
  isDarkPreview: boolean
  isPhone: boolean
}

const AdminUiConfigurationPageFn = React.memo(
  ({ uiConfig, tempUiConfig, isDarkPreview, isPhone }: IAdminUiConfigurationPageProps) => {
    // first render useEffect
    React.useEffect(() => {
      if (hasUiConfigurationChanges()) {
        resetUiConfigChanges()
      }
    }, [])
    return (
      <FlexBox vertical fullWidth fullHeight>
        <PageHeader title={translate("admin.ui-configuration.page.title")} />
        <FlexBox
          vertical
          fullWidth
          fullHeight={PAGE_HEADER_HEIGHT_IN_REM}
          gap
          id={UI_CONFIG_PAGE_DIV_ID}
          jc="start"
          sx={{
            position: "relative",
            overflow: "auto"
          }}>
          <UiConfigHeader isDarkPreview={isDarkPreview} uiConfig={uiConfig} tempUiConfig={tempUiConfig} />
          <FlexBox
            vertical
            jc="start"
            sx={{
              p: "1rem",
              pt: 0
            }}>
            <ContentBox
              id={UI_CONFIG_PREVIEW_DIV_ID}
              icon={faMagnifyingGlass}
              title={translate("admin.ui-configuration.preview.title")}>
              <UiConfigPreview isDark={isDarkPreview} uiConfig={tempUiConfig} isPhone={isPhone} />
            </ContentBox>
            <ContentBox id={UI_CONFIG_FONT_DIV_ID} icon={faFont} title={translate("admin.ui-configuration.font.title")}>
              <UiConfigFont uiConfig={tempUiConfig} />
            </ContentBox>
            <ContentBox icon={faPalette} title={translate("admin.ui-configuration.colors.title")}>
              <UiConfigColors uiConfig={tempUiConfig} />
            </ContentBox>
            <ContentBox icon={faInputText} title={translate("admin.ui-configuration.content.title")}>
              <FlexBox gap="2rem" wrap ai="start">
                <UiConfigTranslations uiConfig={tempUiConfig} />
                <UiConfigPageTitle uiConfig={tempUiConfig} />
              </FlexBox>
            </ContentBox>
            {!isPhone && (
              <ContentBox icon={faImage} title={translate("admin.ui-configuration.images.title")}>
                <UiConfigFilesUpload uiConfig={tempUiConfig} />
              </ContentBox>
            )}
          </FlexBox>
        </FlexBox>
      </FlexBox>
    )
  }
)

export const AdminUiConfigurationPage = connect(
  (state: IRootState): IAdminUiConfigurationPageProps => ({
    uiConfig: state.ui.config,
    tempUiConfig: state.ui.tempConfig,
    isDarkPreview: state.ui.isDarkPreview,
    isPhone: isPhoneScreen(state)
  })
)(AdminUiConfigurationPageFn)
